import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { listGeographyNodes, type GeoNodeOption } from '../../api/brandingWorkOrderApi.js';

export interface GeoSelection {
  /** '' means the whole country — no geography was narrowed at all. */
  readonly geoLevelCode: string;
  readonly geoNodeId: string;
  readonly geoNodeCode: string;
  readonly geoNodeName: string;
  /** Every node picked, State first, ending at `geoNodeId`. */
  readonly pathNodeIds: readonly string[];
}

export const EMPTY_GEO: GeoSelection = {
  geoLevelCode: '',
  geoNodeId: '',
  geoNodeCode: '',
  geoNodeName: '',
  pathNodeIds: [],
};

interface Level {
  readonly code: string;
  readonly label: string;
  /** Shown as the blank option once a parent is chosen. */
  readonly allLabel: string;
}

const LADDER: readonly Level[] = [
  { code: 'GEO_STATE', label: 'State', allLabel: 'All India' },
  { code: 'GEO_DISTRICT', label: 'District', allLabel: 'All Districts' },
  { code: 'GEO_TEHSIL', label: 'Sub-district / Tehsil', allLabel: 'All Sub-districts' },
  { code: 'GEO_CITY', label: 'City', allLabel: 'All Cities' },
];

interface Props {
  readonly idPrefix: string;
  readonly testIdPrefix: string;
  readonly onChange: (sel: GeoSelection) => void;
  /** Stops the ladder here, for callers whose storage cannot hold deeper. */
  readonly deepestLevelCode?: string;
}

/**
 * State → District → Sub-district → City, picked from lists rather than typed.
 *
 * Stopping at a level is the way to say "everything below it": choosing
 * Rajasthan and leaving District on "All Districts" records the order against
 * the state itself, so one order can cover an entire state without listing its
 * districts.
 */
export function GeographyCascade({ idPrefix, testIdPrefix, onChange, deepestLevelCode }: Props): ReactNode {
  const { token } = useAuth();
  const [options, setOptions] = useState<Record<string, readonly GeoNodeOption[]>>({});
  const [picked, setPicked] = useState<Record<string, GeoNodeOption | null>>({});
  const [error, setError] = useState<string | null>(null);

  const cut = deepestLevelCode ? LADDER.findIndex((l) => l.code === deepestLevelCode) : -1;
  const ladder = cut === -1 ? LADDER : LADDER.slice(0, cut + 1);

  // A level is offered only when its parent is a specific node.
  const parentOf = (i: number) => (i === 0 ? undefined : picked[ladder[i - 1].code]?.id);
  const firstUnreachable = ladder.findIndex((_, i) => i > 0 && !parentOf(i));
  const visibleCount = firstUnreachable === -1 ? ladder.length : firstUnreachable;

  useEffect(() => {
    let alive = true;
    const level = LADDER[0].code;
    listGeographyNodes(level, undefined, token)
      .then((r) => { if (alive) setOptions((o) => ({ ...o, [level]: r })); })
      .catch((e) => { if (alive) setError(`States could not be loaded: ${(e as Error).message}`); });
    return () => { alive = false; };
  }, [token]);

  const choose = (index: number, id: string) => {
    const level = ladder[index].code;
    const node = (options[level] ?? []).find((o) => o.id === id) ?? null;

    // Anything below the changed level no longer applies.
    const next: Record<string, GeoNodeOption | null> = {};
    for (let i = 0; i < index; i += 1) next[ladder[i].code] = picked[ladder[i].code] ?? null;
    next[level] = node;
    setPicked(next);

    const deepest = [...ladder].reverse().find((l) => next[l.code]);
    const chosen = deepest ? next[deepest.code]! : null;
    onChange(
      deepest && chosen
        ? {
            geoLevelCode: deepest.code,
            geoNodeId: chosen.id,
            geoNodeCode: chosen.code,
            geoNodeName: chosen.name,
            pathNodeIds: ladder.map((l) => next[l.code]?.id).filter((id): id is string => !!id),
          }
        : EMPTY_GEO,
    );

    // Load the child list now that this level has a specific parent.
    const child = ladder[index + 1];
    if (child && node) {
      listGeographyNodes(child.code, node.id, token)
        .then((r) => setOptions((o) => ({ ...o, [child.code]: r })))
        .catch((e) => setError(`${child.label} list could not be loaded: ${(e as Error).message}`));
    }
  };

  return (
    <>
      {error ? <span role="alert">{error}</span> : null}
      {ladder.slice(0, visibleCount).map((l, i) => (
        <span key={l.code}>
          <label htmlFor={`${idPrefix}-${l.code}`}>{l.label}</label>
          <select
            id={`${idPrefix}-${l.code}`}
            value={picked[l.code]?.id ?? ''}
            onChange={(e) => choose(i, e.target.value)}
            data-testid={`${testIdPrefix}-${l.code.toLowerCase()}`}
          >
            <option value="">{l.allLabel}</option>
            {(options[l.code] ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </span>
      ))}
    </>
  );
}
